import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PureQueryOptions } from '@apollo/client';

import { MainTemplate } from 'templates';
import { GroupsOverviewListDocument } from 'core/graphql';
import {
  IBrowserConversation,
  useCommsRefresh,
  useCommunicatorDeepLink,
  useGroupCommsIndex,
  useGroupsOverview,
} from 'core/hooks';

import { GroupsColumn } from './GroupsColumn';
import { StudentsColumn } from './StudentsColumn';
import { ContextTabs } from './ContextTabs';
import { SettingsDrawer } from './SettingsDrawer';
import * as S from './Communicator.styled';

/**
 * Communicator — the unified communication hub: groups on the left, the
 * selected group's students + the pinned "group channels" item in the middle,
 * and the Slack / Почта / Задачи tabs for the selected context on the right.
 * The whole view state lives in the URL (`useCommunicatorDeepLink`), so any
 * screen is shareable and survives a reload.
 */
export const Communicator = memo(() => {
  const { t } = useTranslation();
  const {
    state,
    setGroup,
    setStudent,
    setChannelsCtx,
    setTab,
    openConversation,
    setQuery,
  } = useCommunicatorDeepLink();

  const {
    students,
    channelsUnread,
    channelsUnanswered,
    mailThreads,
    loading: commsLoading,
    badgeRefetch,
  } = useGroupCommsIndex(state.group);

  // Unfiltered rollup for the TaskBoard create-form group picker (a search in
  // the groups column must not shrink the picker — same trick as the
  // dashboard's second cache entry).
  const { groups: pickerGroups } = useGroupsOverview();

  // Triage mutations refetch both badge sources: the group sweep (middle
  // column) and the overview rollup (left column, same variables as
  // GroupsColumn's query).
  const extraRefetchQueries = useMemo<PureQueryOptions[]>(
    () => [
      badgeRefetch,
      {
        query: GroupsOverviewListDocument,
        variables: { search: state.query || null, onlyIssues: false },
      },
    ],
    [badgeRefetch, state.query],
  );

  // «Обновить сообщения»: with a group selected syncs that workspace +
  // mailboxes, otherwise everything (same semantics as the group card).
  const { refresh, syncing } = useCommsRefresh(state.group || undefined);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  // A mail-digest row jumps into that student's mail context with the thread
  // preselected (thread without a student keeps the group context).
  const onOpenMailThread = useCallback(
    (thread: IBrowserConversation) => {
      openConversation(thread.id, {
        student: thread.student?.id || '',
        ctx: '',
        tab: 'mail',
      });
    },
    [openConversation],
  );

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.HeaderRow>
          <S.PageTitle>{t('pages.communicator.title')}</S.PageTitle>
          <S.HeaderActions>
            {state.group ? (
              <S.GhostButton type="button" onClick={openSettings}>
                {t('pages.communicator.settings')}
              </S.GhostButton>
            ) : null}
            <S.HeaderButton type="button" disabled={syncing} onClick={refresh}>
              {state.group
                ? t('pages.communications.syncGroup')
                : t('pages.communications.syncAll')}
            </S.HeaderButton>
          </S.HeaderActions>
        </S.HeaderRow>
        <S.SubTitle>{t('pages.communicator.subtitle')}</S.SubTitle>

        <S.Layout>
          <GroupsColumn
            query={state.query}
            selectedGroupId={state.group}
            onQuery={setQuery}
            onSelect={setGroup}
          />

          {state.group ? (
            <StudentsColumn
              students={students}
              channelsUnread={channelsUnread}
              channelsUnanswered={channelsUnanswered}
              loading={commsLoading}
              selectedStudentId={state.student}
              ctx={state.ctx}
              onSelectStudent={setStudent}
              onSelectChannels={setChannelsCtx}
            />
          ) : (
            <S.Pane>
              <S.PaneHeader>
                {t('pages.communicator.studentsLabel')}
              </S.PaneHeader>
              <S.Muted>{t('pages.communicator.selectGroup')}</S.Muted>
            </S.Pane>
          )}

          <S.MainPane>
            <ContextTabs
              groupId={state.group}
              studentId={state.student}
              ctx={state.ctx}
              tab={state.tab}
              conv={state.conv}
              mailThreads={mailThreads}
              commsLoading={commsLoading}
              extraRefetchQueries={extraRefetchQueries}
              pickerGroups={pickerGroups}
              onTab={setTab}
              onOpenMailThread={onOpenMailThread}
            />
          </S.MainPane>
        </S.Layout>

        <SettingsDrawer
          groupId={state.group}
          studentId={state.student || undefined}
          visible={settingsOpen}
          onClose={closeSettings}
        />
      </S.Wrapper>
    </MainTemplate>
  );
});

Communicator.displayName = 'Communicator';
